/**
 * Поведение ползунка и таблица значений.
 *
 * Чистые правила без React: и то и другое проверяется тестом, а не глазами.
 */

import { exact, formatApprox, type ExactNumber } from "./numbers";
import { curveFeatures, valuesAt } from "./math";
import { isRelation, type LabScene, type SceneCurve } from "./model";
import { gridStep, worldBounds, type PlotCamera, type PlotSize } from "./viewport";

const BASE_BOUND = 10;

/**
 * Границы ползунка. Точный ввод шагом не ограничен, поэтому введённое
 * значение обязано попадать в шкалу: иначе ползунок показывает не то число,
 * которое стоит в поле.
 */
export function sliderBound(value: number, base = BASE_BOUND): number {
  let bound = base;
  while (Math.abs(value) > bound && bound < 1e6) bound *= 2;
  return bound;
}

/** Основной шаг `0.5`; на расширенной шкале он растёт вместе с ней. */
export function sliderStep(bound: number): number {
  if (bound <= BASE_BOUND) return 0.5;
  const raw = bound / 20;
  const power = 10 ** Math.floor(Math.log10(raw));
  const base = raw / power;
  return (base <= 1 ? 1 : base <= 2 ? 2 : base <= 5 ? 5 : 10) * power;
}

/** Шаги, между которыми ученик переключает ползунок. */
export const STEP_CHOICES = [0.1, 0.25, 0.5, 1, 2];

/**
 * Рабочий шаг. Выбранный шаг действует, пока шкала обычная; на растянутой
 * он не может быть мельче её собственного — при значении 300 шкала уходит до
 * ±320, и шаг 0,1 даёт шесть тысяч позиций, то есть ползунок, который не
 * слушается ни мыши, ни стрелок.
 */
export function effectiveStep(chosen: number, bound: number): number {
  return bound <= BASE_BOUND ? chosen : Math.max(chosen, sliderStep(bound));
}

/**
 * Шаг в тексте. Точка, а не запятая: коэффициент в формуле печатается через
 * точку (`2.5`), и две разные записи дробного числа в одной карточке читаются
 * как две разные величины.
 */
export function stepLabel(step: number): string {
  return String(step);
}

/**
 * Прилипание при перетягивании. Ноль и ±1 держат сильнее шага: именно на них
 * коэффициент меняет смысл, и промахнуться мимо них обиднее всего.
 */
export function snapSlider(raw: number, step: number): ExactNumber {
  for (const anchor of [0, 1, -1]) {
    if (Math.abs(raw - anchor) < step * 0.4) return exact(anchor);
  }
  return exact(Math.round(raw / step) * step);
}

export type TableCell = { id: string; text: string };
export type TableRow = { x: number; label: string; cells: TableCell[] };

/** Огромное число в таблице бесполезно и ломает колонку — пишем «не определено». */
const HUGE = 1e7;

/**
 * Ячейка таблицы и строка пробника. У окружности при одном `x` два `y`, и
 * они печатаются оба: «−1 и 3» — это и есть ответ на вопрос, почему она не
 * функция. Пустоту функции и пустоту окружности называем по-разному:
 * функция в точке «не определена», а вертикальная прямая просто прошла мимо
 * окружности — «нет точек».
 */
export function cellText(curve: SceneCurve["curve"], x: number): string {
  const values = valuesAt(curve, x).filter(
    (value) => Number.isFinite(value) && Math.abs(value) < HUGE,
  );
  if (values.length === 0) return isRelation(curve.kind) ? "нет точек" : "не определено";
  return values.map((value) => formatApprox(value, 3).replace(/^-/, "−")).join(" и ");
}

/**
 * Таблица значений — текстовая альтернатива графику. Берёт круглые `x` около
 * центра камеры, `x` пробника и характерные `x` активной кривой.
 */
export function valueTable(
  scene: LabScene,
  camera: PlotCamera,
  size: PlotSize,
  probeX: number | null,
): TableRow[] {
  const visible = scene.curves.filter((curve) => curve.visible);
  const bounds = worldBounds(camera, size);
  const step = Math.max(1, Math.round(gridStep(camera.scale, 90)));
  const center = Math.round(camera.cx / step) * step;

  const wanted: Array<{ x: number; label: string }> = [];
  for (let index = -3; index <= 3; index++) {
    wanted.push({ x: center + index * step, label: "" });
  }
  if (probeX !== null && Number.isFinite(probeX)) {
    wanted.push({ x: probeX, label: "пробник" });
  }
  const active = scene.curves.find((curve) => curve.id === scene.activeId);
  if (active) {
    for (const point of curveFeatures(active.curve).points) {
      if (point.x.value >= bounds.minX && point.x.value <= bounds.maxX) {
        wanted.push({ x: point.x.value, label: point.label });
      }
    }
  }

  const rows: TableRow[] = [];
  for (const { x, label } of wanted.sort((a, b) => a.x - b.x)) {
    if (!Number.isFinite(x)) continue;
    const same = rows.find((row) => Math.abs(row.x - x) < 1e-9);
    if (same) {
      if (label && !same.label) same.label = label;
      continue;
    }
    if (rows.length >= 14) continue;
    rows.push({
      x,
      label,
      cells: visible.map((curve) => ({ id: curve.id, text: cellText(curve.curve, x) })),
    });
  }
  return rows;
}
